import { z } from 'zod';
import { SaleSchema, type SaleData, getSaleOrderDetails } from '@am3nd-org/pointcraft-types';

import { computeSnapshotHash } from 'src/version-5/payment/engine/resolve-sale-context';

import {
  type KdsOrder,
  KDS_UNNAMED_ITEM,
  type KdsOrderItem,
  KDS_UNNAMED_MODIFIER,
} from '../domain/kds-order.types';

export const ORDER_PAYMENT_STATES = [
  'UNPAID',
  'PARTIAL',
  'PAID',
  'PARTIALLY_REFUNDED',
  'REFUNDED',
  'REPORTING_PENDING',
  'UNKNOWN',
  'CANCELLED',
  /** Staff meal: nothing is owed and no payment can be claimed. */
  'NO_CHARGE',
] as const;
export type OrderPaymentState = (typeof ORDER_PAYMENT_STATES)[number];
export const ORDER_LEASE_MS = 120_000;
export const ORDER_HEARTBEAT_MS = 30_000;

export function kdsOrderId(order: Pick<KdsOrder, 'orderId' | 'saleId'>): string {
  const id = order.orderId ?? order.saleId;
  if (!id) throw new Error('The order has no stable identity.');
  return id;
}

export function orderIdForSale(sale: SaleData): string {
  const details = getSaleOrderDetails(sale);
  return z
    .string()
    .uuid()
    .parse(details?.orderId ?? sale.id);
}

/** Firestore stores a checkout, never financial journals or provider credentials. */
export function serializeOrderCheckout(input: SaleData): Record<string, unknown> {
  const sale = SaleSchema.parse({
    ...input,
    data: [],
    orderDetails: getSaleOrderDetails(input),
    salesMeta: {
      ...input.salesMeta,
      employee: null,
      order: null,
      customer: input.salesMeta.customer
        ? { id: input.salesMeta.customer.id, name: input.salesMeta.customer.name }
        : null,
    },
    paymentInfo: {
      pendingPayments: [],
      completedPayments: [],
      failedPayments: [],
      refundedPayments: [],
      totalAmount: 0,
    },
  });
  const serialized = JSON.stringify(sale, (key, value) => {
    if (
      /^(?:password|secret|clientSecret|credentials?|accessToken|refreshToken|idToken|token|authorization|cardNumber|cvv|signingKey|privateKey|apiKey)$/i.test(
        key
      )
    ) {
      return undefined;
    }
    return value;
  });
  if (new TextEncoder().encode(serialized).byteLength > 500_000) {
    throw new Error('This order is too large to send to the kitchen.');
  }
  return JSON.parse(serialized) as Record<string, unknown>;
}

export function restoreOrderCheckout(value: unknown): SaleData {
  return SaleSchema.parse(value);
}

/** Excludes descriptive guest details and mutable UI/payment state. */
export function orderCheckoutFingerprint(sale: SaleData): string {
  return computeSnapshotHash({
    items: sale.items.map(
      ({
        id,
        identifier,
        name,
        price,
        quantity,
        productType,
        metadata,
        linkedProduct,
        linkedMobileService,
      }) => ({
        id,
        identifier,
        name,
        price,
        quantity,
        productType,
        metadata,
        inventoryId: linkedProduct?.id ?? null,
        mobileServiceId: linkedMobileService?.id ?? null,
      })
    ),
    financialInfo: {
      subTotal: sale.financialInfo.subTotal,
      taxes: sale.financialInfo.taxes,
      discount: sale.financialInfo.discount,
      adjustment: sale.financialInfo.adjustment,
      shippingCharges: sale.financialInfo.shippingCharges,
    },
    locationId: sale.salesMeta.businessLocation.id,
    customerId: sale.salesMeta.customer?.id ?? null,
    billingControls: sale.salesMeta.billingControls,
  });
}

const KITCHEN_ITEM_LIMIT = 500;

/**
 * The strict kitchen item shape — the one every KDS build reads, including
 * older strict readers still in the field. Saving anything looser would let
 * one order freeze those kitchen boards, so writes are validated against it.
 */
const kitchenItemsWriteSchema = z
  .array(
    z
      .object({
        id: z.string().min(1),
        productId: z.string().min(1).optional(),
        name: z.string().min(1),
        quantity: z.number().finite().positive(),
        modifiers: z.array(
          z
            .object({
              id: z.string().min(1),
              name: z.string().min(1),
              quantity: z.number().int().positive(),
            })
            .strict()
        ),
        specialInstructions: z.string().min(1).optional(),
      })
      .strict()
  )
  .min(1)
  .max(KITCHEN_ITEM_LIMIT);

/**
 * Kitchen lines for an order. Blank names become placeholders and a blank
 * modifier id is derived from its group, but a modifier is never dropped (it
 * may carry an allergy note). A quantity the kitchen cannot act on is rejected
 * before anything is saved.
 */
export function kitchenItems(sale: SaleData): KdsOrderItem[] {
  if (!sale.items.length) throw new Error('Add items before sending an order.');
  if (sale.items.length > KITCHEN_ITEM_LIMIT) {
    throw new Error(`An order can hold at most ${KITCHEN_ITEM_LIMIT} lines.`);
  }
  const items = sale.items.map((item) => {
    const name = item.name.trim() || KDS_UNNAMED_ITEM;
    if (!item.id) throw new Error(`"${name}" is missing its line id. Remove it and add it again.`);
    if (!Number.isFinite(item.quantity) || item.quantity <= 0) {
      throw new Error(`"${name}" needs a quantity above zero before it can go to the kitchen.`);
    }
    const modifiers = (item.metadata?.selectedModifierData ?? []).flatMap((group, groupIndex) =>
      group.selected.map((modifier, index) => {
        const modifierName = modifier.name.trim() || KDS_UNNAMED_MODIFIER;
        if (!Number.isInteger(modifier.quantity) || modifier.quantity <= 0) {
          throw new Error(
            `"${modifierName}" on "${name}" needs a whole-number quantity above zero.`
          );
        }
        return {
          id: modifier.modifierId.trim() || `${group.groupId || `group-${groupIndex}`}:${index}`,
          name: modifierName,
          quantity: modifier.quantity,
        };
      })
    );
    const specialInstructions = item.metadata?.specialInstructions?.trim();
    return {
      id: item.id,
      ...(item.linkedProduct?.id ? { productId: item.linkedProduct.id } : {}),
      name,
      quantity: item.quantity,
      modifiers,
      ...(specialInstructions ? { specialInstructions } : {}),
    };
  });
  return kitchenItemsWriteSchema.parse(items);
}

export function canAmendOrder(order: KdsOrder): boolean {
  return (
    order.schemaVersion === 2 &&
    order.source === 'IN_STORE' &&
    order.paymentState === 'UNPAID' &&
    !order.financiallyFrozen &&
    ['WAITING', 'PREPARING'].includes(order.status)
  );
}

/**
 * Why an order cannot move to another table. Moving is an amendment (see
 * `move-order-table.ts`), so `NOT_AMENDABLE` covers paid, Ready, online and
 * legacy orders; staff meals and closed orders have no table to change.
 */
export type TableMoveRefusal = 'STAFF_MEAL' | 'CLOSED' | 'NOT_AMENDABLE';

export const TABLE_MOVE_REFUSAL_COPY: Record<TableMoveRefusal, string> = {
  STAFF_MEAL: 'Staff meals are not seated at a table.',
  CLOSED: 'This order is closed, so its table stays.',
  NOT_AMENDABLE: 'Only unpaid orders still in the kitchen can change tables for now.',
};

export function tableMoveRefusal(order: KdsOrder): TableMoveRefusal | null {
  if (order.fulfillmentType === 'STAFF_MEAL') return 'STAFF_MEAL';
  if (['COMPLETED', 'CANCELLED', 'ARCHIVED'].includes(order.status)) return 'CLOSED';
  return canAmendOrder(order) ? null : 'NOT_AMENDABLE';
}

export function orderDraftFingerprint(sale: SaleData): string {
  return computeSnapshotHash({
    checkout: orderCheckoutFingerprint(sale),
    orderDetails: getSaleOrderDetails(sale),
  });
}

export function orderPaymentLabel(state?: OrderPaymentState): string {
  const labels: Record<OrderPaymentState, string> = {
    UNPAID: 'Unpaid',
    PARTIAL: 'Partially paid',
    PAID: 'Paid',
    PARTIALLY_REFUNDED: 'Partially refunded',
    REFUNDED: 'Refunded',
    REPORTING_PENDING: 'Payment reporting pending',
    UNKNOWN: 'Payment needs verification',
    CANCELLED: 'Sale cancelled',
    NO_CHARGE: 'No charge',
  };
  return state ? labels[state] : 'Payment status unverified';
}
